import {Tabs} from '@jetbrains/ring-ui/components/tabs/tabs'
import {skipToken} from '@reduxjs/toolkit/query'
import * as React from 'react'

import Measure from '../../../../containers/Measure'
import {useAppSelector} from '../../../../hooks/react-redux'
import {getExtensionEndpoint} from '../../../../selectors'
import {restApi} from '../../../../services/rest'
import type {BuildId} from '../../../../types'
import Loader from '../../../common/Loader/Loader'
import OverviewTab, {renderTitle} from '../../../common/OverviewTab/OverviewTab'
import type {CompareBuildsTab} from '../CompareBuildsPage.types'
import CompareBuildsTable from '../CompareBuildsTable/CompareBuildsTable.container'

const TABLE_IS_WIDE_PX = 1200
type Props = {
  sourceId: BuildId
  compareWithId: BuildId | null | undefined
  onTabSelect: (tab: string) => unknown
  tab?: string | null
}

const getTabUrl = (tab: string): string => {
  const currentSearch = new URLSearchParams(location.search)
  currentSearch.set('tab', tab)
  return `?${currentSearch.toString()}`
}

function CompareBuildsTabs({sourceId, compareWithId, tab, onTabSelect}: Props): React.ReactNode {
  const endpoint = useAppSelector(state => getExtensionEndpoint(state, 'builds-diff')?.endpoint)
  const {list} = restApi.endpoints.getCompareBuildsList.useQuery(
    endpoint != null && compareWithId != null ? {endpoint, sourceId, compareWithId} : skipToken,
    {
      selectFromResult: ({data}) => ({list: data}),
    },
  )

  if (list == null) {
    return <Loader />
  }

  let currentTab: string | undefined

  if (list.length > 0) {
    if (tab != null) {
      currentTab = tab
    } else {
      currentTab = list[0].type
    }
  }

  return (
    <Tabs onSelect={onTabSelect} selected={currentTab}>
      {list.map((it: CompareBuildsTab) => (
        <OverviewTab
          key={it.type}
          id={it.type}
          title={renderTitle(it.displayName)}
          href={getTabUrl(it.type)}
          unlimitedWidth
        >
          <Measure offset>
            {({measureRef, contentRect}) => (
              <div ref={measureRef}>
                {currentTab != null && (
                  <CompareBuildsTable
                    type={currentTab}
                    sourceId={sourceId}
                    compareWithId={compareWithId}
                    wide={(contentRect.offset?.width ?? 0) >= TABLE_IS_WIDE_PX}
                  />
                )}
              </div>
            )}
          </Measure>
        </OverviewTab>
      ))}
    </Tabs>
  )
}

export default CompareBuildsTabs
